import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Broom,
  PaperPlaneTilt,
  CircleNotch,
  CheckCircle,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * MaintenanceTile — admin one-click runners for two ops that previously
 * needed shell access:
 *
 *   1. Cleanup placeholder profile_drafts (Test*, DELETE ME, lorem ipsum…).
 *      Hits /api/admin/maintenance/placeholder-drafts/{preview,cleanup}.
 *
 *   2. Profile-nudge blast — sends "complete your profile" email to all
 *      truck-owner users without a published profile. Two templates
 *      (urgent Pro / soft Free). Test-domain emails excluded server-side.
 *      Hits /api/admin/maintenance/profile-nudge/{preview,blast,blast/:id}.
 *      Sending is async (background task on the backend, throttled 0.4s
 *      between sends); UI polls the job-status endpoint every 2s.
 */
export default function MaintenanceTile() {
  return (
    <div className="space-y-6">
      <PlaceholderCleanupSection />
      <ProfileNudgeBlastSection />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function PlaceholderCleanupSection() {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/maintenance/placeholder-drafts/preview");
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runCleanup = async () => {
    if (!preview?.count) return;
    const ok = window.confirm(
      `Hard-delete ${preview.count} placeholder profile_draft rows from MongoDB? This cannot be undone.`,
    );
    if (!ok) return;
    setRunning(true);
    try {
      const { data } = await api.post(
        "/admin/maintenance/placeholder-drafts/cleanup",
        { confirm: true },
      );
      toast.success(`Deleted ${data.deleted} placeholder rows.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="admin-cleanup-placeholder-drafts"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <Broom weight="duotone" className="h-5 w-5 text-primary" />
            Cleanup placeholder profile drafts
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Hard-delete published drafts with junk names (Test*, DELETE ME, PLACEHOLDER, lorem ipsum, asdf qwerty, tbd, blank).
            These were leaking into real event-inquiry matches.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading || running}
          data-testid="admin-cleanup-refresh"
        >
          {loading ? <CircleNotch className="h-3 w-3 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {err && (
        <p className="text-sm text-rose-600 py-2" data-testid="admin-cleanup-error">
          {err}
        </p>
      )}

      {preview && (
        <>
          <div className="flex items-center justify-between text-sm py-2 border-y border-border/50">
            <span className="text-muted-foreground">Junk rows in profile_drafts</span>
            <span className="font-bold text-lg" data-testid="admin-cleanup-count">
              {preview.count}
            </span>
          </div>

          {preview.count > 0 && (
            <>
              <div className="text-xs text-muted-foreground mt-3 mb-1.5 uppercase tracking-wide">
                Sample ({Math.min(preview.sample?.length || 0, 30)} of {preview.count})
              </div>
              <div className="max-h-48 overflow-y-auto rounded-md bg-muted/30 px-3 py-2 space-y-1 text-xs font-mono">
                {(preview.sample || []).map((r, i) => (
                  <div key={i} className="truncate">
                    <span className={r.is_published ? "text-rose-600 font-bold" : "text-muted-foreground"}>
                      [{r.is_published ? "PUB" : "drf"}]
                    </span>{" "}
                    {r.truck_name || "(empty name)"}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end">
                <Button
                  variant="destructive"
                  onClick={runCleanup}
                  disabled={running || preview.count === 0}
                  data-testid="admin-cleanup-run"
                >
                  {running ? (
                    <><CircleNotch className="h-4 w-4 mr-2 animate-spin" /> Deleting…</>
                  ) : (
                    <>Delete {preview.count} rows</>
                  )}
                </Button>
              </div>
            </>
          )}

          {preview.count === 0 && (
            <p className="text-sm text-emerald-700 flex items-center gap-1.5 mt-2">
              <CheckCircle weight="fill" className="h-4 w-4" /> Clean — no junk drafts.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function ProfileNudgeBlastSection() {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [onlyEmail, setOnlyEmail] = useState("");
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/admin/maintenance/profile-nudge/preview");
      setPreview(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Poll the running job every 2s
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/maintenance/profile-nudge/blast/${jobId}`);
        if (cancelled) return;
        setJobStatus(data);
        if (data.status === "done" || data.status === "error") {
          if (pollRef.current) clearInterval(pollRef.current);
          if (data.status === "done") {
            toast.success(`Blast finished: sent ${data.sent}, failed ${data.failed}.`);
          } else {
            toast.error(`Blast errored: ${data.error || "unknown"}`);
          }
          // Refresh audience preview — published owners may have changed
          await load();
        }
      } catch (e) {
        /* keep polling on transient errors */
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [jobId]);

  const startBlast = async (only) => {
    const label = only
      ? `Send a smoke-test email to ${only}?`
      : `Send the blast to ALL ${preview?.total} truck owners (${preview?.pro_count} Pro + ${preview?.free_count} Free)?`;
    if (!window.confirm(label)) return;
    setJobStatus(null);
    try {
      const { data } = await api.post("/admin/maintenance/profile-nudge/blast", {
        confirm: true,
        only: only || null,
      });
      setJobId(data.job_id);
      const synthMsg = data.synthetic_preview
        ? " (preview render — your profile is complete, so we're showing the most-missing variant)"
        : "";
      toast.message(
        `Blast queued — ${data.audience_size} email${data.audience_size === 1 ? "" : "s"} will be sent (throttled 0.4s)${synthMsg}.`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalForJob = jobStatus?.total || 0;
  const doneForJob = (jobStatus?.sent || 0) + (jobStatus?.failed || 0);
  const pct = totalForJob ? Math.round((doneForJob / totalForJob) * 100) : 0;
  const isRunning = jobStatus && jobStatus.status === "running";
  const isQueued = jobStatus && jobStatus.status === "queued";

  return (
    <div
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="admin-profile-nudge-blast"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-base font-semibold flex items-center gap-2">
            <PaperPlaneTilt weight="duotone" className="h-5 w-5 text-primary" />
            Profile-nudge blast
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Email truck owners whose profile isn&apos;t complete. Tier-aware criteria — see below. Test-domain emails skipped.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-blast-refresh"
        >
          {loading ? <CircleNotch className="h-3 w-3 animate-spin" /> : "Refresh"}
        </Button>
      </div>

      {err && (
        <p className="text-sm text-rose-600 py-2" data-testid="admin-blast-error">
          {err}
        </p>
      )}

      {preview && (
        <>
          <div className="grid grid-cols-3 gap-3 my-3 text-center">
            <div className="rounded-lg bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Pro</div>
              <div className="text-2xl font-bold" data-testid="admin-blast-pro-count">
                {preview.pro_count}
              </div>
            </div>
            <div className="rounded-lg bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Free</div>
              <div className="text-2xl font-bold" data-testid="admin-blast-free-count">
                {preview.free_count}
              </div>
            </div>
            <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Total</div>
              <div className="text-2xl font-bold text-primary" data-testid="admin-blast-total">
                {preview.total}
              </div>
            </div>
          </div>

          {preview.criteria && (
            <div className="rounded-md border border-border bg-muted/30 px-3 py-2 my-3 text-xs">
              <div className="font-semibold uppercase tracking-wide text-muted-foreground mb-1">Completeness criteria</div>
              <div><span className="font-semibold">Free:</span> {preview.criteria.free}</div>
              <div><span className="font-semibold">Pro:</span> {preview.criteria.pro}</div>
            </div>
          )}

          {preview.skipped_test_emails > 0 && (
            <p className="text-[11px] text-muted-foreground mb-3">
              Skipping {preview.skipped_test_emails} test-domain emails (@example.com, @followtest.app, etc.)
            </p>
          )}

          {/* Audience preview with missing-fields per row */}
          {(preview.pro_audience?.length > 0 || preview.free_audience?.length > 0) && (
            <details className="mb-3 rounded-md border border-border bg-card" data-testid="admin-blast-audience-details">
              <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:bg-muted/30">
                Audience breakdown · {preview.total} truck owner{preview.total === 1 ? "" : "s"}
              </summary>
              <div className="max-h-72 overflow-y-auto px-3 pb-3 space-y-1 text-xs font-mono">
                {preview.pro_audience?.map((u, i) => (
                  <AudienceRow key={`p-${i}`} user={u} tier="PRO" />
                ))}
                {preview.free_audience?.map((u, i) => (
                  <AudienceRow key={`f-${i}`} user={u} tier="FREE" />
                ))}
              </div>
            </details>
          )}

          {/* Smoke-test row */}
          <div className="rounded-md border border-border bg-muted/20 p-3 my-2">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Smoke test (send to just one address first)
            </div>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="your@email.com"
                value={onlyEmail}
                onChange={(e) => setOnlyEmail(e.target.value)}
                disabled={isRunning || isQueued}
                data-testid="admin-blast-only-input"
              />
              <Button
                variant="outline"
                onClick={() => startBlast(onlyEmail.trim())}
                disabled={!onlyEmail.trim() || isRunning || isQueued}
                data-testid="admin-blast-only-btn"
              >
                Send test
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">
              Any real user email works. If the address isn&apos;t in the live audience (e.g. your profile is complete), we render a preview with all missing fields so you can vet the template.
            </p>
          </div>

          {/* Full blast row */}
          <div className="mt-4 flex justify-end">
            <Button
              onClick={() => startBlast(null)}
              disabled={
                !preview.total || isRunning || isQueued
              }
              data-testid="admin-blast-run"
            >
              {isRunning || isQueued ? (
                <><CircleNotch className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <>Send to all {preview.total}</>
              )}
            </Button>
          </div>

          {/* Live job status */}
          {jobStatus && (
            <div className="mt-4 rounded-md border border-border bg-card p-3" data-testid="admin-blast-status">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase font-semibold tracking-wide text-muted-foreground">
                  Job status
                </span>
                <StatusPill status={jobStatus.status} />
              </div>
              <div className="text-sm font-mono mb-2">
                {jobStatus.sent}/{totalForJob} sent · {jobStatus.failed} failed
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              {jobStatus.results && jobStatus.results.length > 0 && (
                <div className="max-h-40 overflow-y-auto mt-3 text-xs font-mono space-y-0.5">
                  {jobStatus.results.slice(-25).reverse().map((r, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      {r.ok ? (
                        <CheckCircle weight="fill" className="h-3 w-3 text-emerald-500 flex-none" />
                      ) : (
                        <Warning weight="fill" className="h-3 w-3 text-rose-500 flex-none" />
                      )}
                      <span className="truncate">{r.email}</span>
                      <span className="text-muted-foreground flex-none uppercase text-[10px]">{r.tier}</span>
                    </div>
                  ))}
                </div>
              )}
              {jobStatus.error && (
                <p className="text-xs text-rose-600 mt-2 break-all">
                  Error: {jobStatus.error}
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const cls = {
    queued: "bg-amber-100 text-amber-700",
    running: "bg-blue-100 text-blue-700",
    done: "bg-emerald-100 text-emerald-700",
    error: "bg-rose-100 text-rose-700",
  }[status] || "bg-muted text-muted-foreground";
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${cls}`}>
      {status}
    </span>
  );
}

function AudienceRow({ user, tier }) {
  const tierCls = tier === "PRO"
    ? "bg-primary/10 text-primary"
    : "bg-muted text-muted-foreground";
  const missing = user.missing || [];
  return (
    <div className="flex items-center gap-2 py-1 border-b border-border/40 last:border-b-0">
      <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded ${tierCls} flex-none`}>
        {tier}
      </span>
      <span className="truncate flex-1">{user.email}</span>
      {missing.length > 0 && (
        <span className="text-[10px] text-rose-600 flex-none">
          needs: {missing.join(", ")}
        </span>
      )}
    </div>
  );
}
